import { useMemo, useState } from 'react';
import { Flex, Input, Text } from '@optiaxiom/react';
import { IconSearch } from '@optimizely/axiom-icons';

import { DirectoryAgent } from '../../services/agent-directory';
import { AgentCardView } from '../specialized-agent/agent-component-view/AgentCardView';

import { styles } from './MarketplaceAgentsGrid.css';

type MarketplaceAgentsGridProps = {
  agents: DirectoryAgent[];
  selectedAgent: DirectoryAgent | undefined;
  setSelectedAgent: (agent: DirectoryAgent) => void;
};

export function MarketplaceAgentsGrid({
  agents,
  selectedAgent,
  setSelectedAgent,
}: MarketplaceAgentsGridProps) {
  const [searchTerm, setSearchTerm] = useState('');

  // Separate featured and regular agents
  const featuredAgents = useMemo(() => {
    return (agents || []).filter(agent => agent.is_featured === true);
  }, [agents]);

  const regularAgents = useMemo(() => {
    return (agents || []).filter(agent => agent.is_featured === false);
  }, [agents]);

  // Apply search filter to both categories
  const filteredFeaturedAgents = useMemo(() => {
    if (!searchTerm.trim()) {
      return featuredAgents;
    }

    const searchLower = searchTerm.toLowerCase();

    return featuredAgents.filter(agent => {
      const nameMatch = agent.name?.toLowerCase().includes(searchLower);
      const descriptionMatch = agent.description?.toLowerCase().includes(searchLower);
      const tagMatch = agent.tags?.some(tag => tag.name?.toLowerCase().includes(searchLower));
      const publisherMatch = agent.publisher_email?.toLowerCase().includes(searchLower);

      return nameMatch || descriptionMatch || tagMatch || publisherMatch;
    });
  }, [featuredAgents, searchTerm]);

  const filteredRegularAgents = useMemo(() => {
    if (!searchTerm.trim()) {
      return regularAgents;
    }

    const searchLower = searchTerm.toLowerCase();

    return regularAgents.filter(agent => {
      const nameMatch = agent.name?.toLowerCase().includes(searchLower);
      const descriptionMatch = agent.description?.toLowerCase().includes(searchLower);
      const tagMatch = agent.tags?.some(tag => tag.name?.toLowerCase().includes(searchLower));
      const publisherMatch = agent.publisher_email?.toLowerCase().includes(searchLower);

      return nameMatch || descriptionMatch || tagMatch || publisherMatch;
    });
  }, [regularAgents, searchTerm]);

  return (
    <Flex flexDirection="column" h="full" gap="16">
      {/* Search Input */}
      <Flex flexDirection="row" alignItems="start">
        <Input
          className={styles.searchInput}
          addonBefore={<IconSearch />}
          id="marketplace-search"
          placeholder="Search agents by name, description, tags"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
      </Flex>

      {/* Featured Agents Section */}
      {filteredFeaturedAgents.length > 0 && (
        <Flex flexDirection="column" gap="16">
          <Text fontSize="lg" fontWeight="600" color="fg.default">
            Featured Agents
          </Text>
          <Flex
            flexWrap="wrap"
            flexDirection="row"
            gap="16"
            justifyContent="flex-start"
            alignItems="start"
            w="full"
            className={styles.agentsContainer}
          >
            {filteredFeaturedAgents.map(agent => (
              <AgentCardView
                key={agent.agent_id}
                agent={agent}
                isSelected={selectedAgent?.agent_id === agent.agent_id}
                onSelect={() => setSelectedAgent(agent)}
              />
            ))}
          </Flex>
        </Flex>
      )}

      {/* Regular Agents Section */}
      {filteredRegularAgents.length > 0 && (
        <Flex flexDirection="column" gap="16">
          <Text fontSize="lg" fontWeight="600" color="fg.default">
            Agents
          </Text>
          <Flex
            flexWrap="wrap"
            flexDirection="row"
            gap="16"
            justifyContent="flex-start"
            alignItems="start"
            w="full"
            className={styles.agentsContainer}
          >
            {filteredRegularAgents.map(agent => (
              <AgentCardView
                key={agent.agent_id}
                agent={agent}
                isSelected={selectedAgent?.agent_id === agent.agent_id}
                onSelect={() => setSelectedAgent(agent)}
              />
            ))}
          </Flex>
        </Flex>
      )}

      {/* No Results Message */}
      {filteredFeaturedAgents.length === 0 && filteredRegularAgents.length === 0 && searchTerm && (
        <Flex justifyContent="center" alignItems="center" h="full">
          <Text color="fg.tertiary">No agents found matching &quot;{searchTerm}&quot;</Text>
        </Flex>
      )}
    </Flex>
  );
}
